import { FC, ReactElement, useState } from "react";
import { Box, Button, Dialog, IconButton, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import { useSnackbar } from "@/hooks";
import { tokens } from "@/tokens";

interface IDeleteConfirmModal {
  open: boolean;
  setOpen?: (open: boolean) => void;
  noPadding?: boolean;
  hideClose?: boolean;
  itemName?: string;
  onClose: () => void;
  onConfirm?: () => Promise<void>;
  maxWidth: "xs" | "sm" | "md" | "lg" | "xl";
  subText?: string;
  btnLoader?: boolean;
}

const DeleteConfirmModal: FC<IDeleteConfirmModal> = ({
  open,
  onClose,
  maxWidth,
  itemName,
  setOpen,
  onConfirm,
  subText,
  btnLoader,
}): ReactElement => {
  const { show } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = (): void => {
    if (!isLoading) {
      if (setOpen) {
        setOpen(false);
      }
    }
  };
  const handleSubmit = (): void => {
    if (onConfirm) {
      setIsLoading(true);
      onConfirm().then(() => {
        setIsLoading(false);
        show("Deleted Successfully!", "", "success");
        handleClose();
      });
    } else {
      handleClose();
    }
  };

  const busy = isLoading || btnLoader;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${tokens.border.default}` }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography sx={{ fontSize: tokens.fontSize.xl, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, lineHeight: 1.3 }}>
              Delete {itemName}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.5 }}>
              Are you sure you want to delete this {subText}? This action cannot be undone.
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: tokens.text.muted, "&:hover": { color: tokens.text.primary } }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{
        px: 3,
        py: 2,
        display: "flex",
        justifyContent: "flex-end",
        gap: 1.5,
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={busy}
          sx={{
            borderColor: tokens.border.default,
            color: tokens.text.secondary,
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            borderRadius: tokens.radius.md,
            textTransform: "none",
            px: 3,
            "&:hover": { borderColor: tokens.text.muted, bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={busy}
          sx={{
            fontWeight: tokens.fontWeight.semibold,
            fontSize: tokens.fontSize.sm2,
            borderRadius: tokens.radius.md,
            textTransform: "none",
            px: 3,
            bgcolor: tokens.status.danger,
            color: "#fff",
            boxShadow: "none",
            "&:hover": { bgcolor: "#dc2626", boxShadow: "none" },
          }}
        >
          Delete
        </Button>
      </Box>
    </Dialog>
  );
};

export default DeleteConfirmModal;
